import { useState } from 'react'

import { Box, Button, Icon, Table, Text } from '@factorialco/deprecated-design-system'

import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { QuotePricingLineItem } from 'modules/finance/components/Quotes/utils'

import AddItemButton from 'modules/finance/components/Quotes/QuotePhasesTable/_Body/AddItemButton'
import ItemRowTimeAndMaterial from 'modules/finance/components/Quotes/QuotePhasesTable/_Body/ItemRowTimeAndMaterial'

interface ItemRowPhaseProps {
  phase: QuotePricingLineItem
  currency: string
  isDraftStatus: boolean
  isJobCatalogTreeEnabled: boolean
  legalEntityCountry: string | undefined
  onAddItem: (phaseId: number, itemType: 'role' | 'simple') => void
  onDeletePhase: () => void
  onUpdatePhase: (updates: Partial<QuotePricingLineItem>) => void
  onUpdateChild: (itemId: number, updates: Partial<QuotePricingLineItem>) => void
  onDeleteChild: (itemId: number) => void
}

const ItemRowPhase = ({
  phase,
  currency,
  isDraftStatus,
  isJobCatalogTreeEnabled,
  legalEntityCountry: _legalEntityCountry,
  onAddItem,
  onDeletePhase,
  onUpdatePhase,
  onUpdateChild,
  onDeleteChild,
}: ItemRowPhaseProps) => {
  const [expanded, setExpanded] = useState(true)
  const {
    access: { locale },
  } = useCurrentSession()
  const {description, quantity, baseCostCents, basePriceCents, baseProfitPercentage} = phase

  return (
    <>
      <Table.Row>
        <Table.Cell paddingX="s12" colSpan={2}>
          <Box flexDirection="row" alignItems="center" gap="s8" width="s300">
          <Box
              cursor="pointer"
              onClick={() => setExpanded((prev) => !prev)}
              paddingRight="s4"
              aria-expanded={expanded}
            >
              <Icon.Medium
                icon={expanded ? 'chevron-down' : 'chevron-right'}
                color="grey800"
              />
            </Box>
            {isDraftStatus ? (
              <TextField
                placeholder={i18n.t('common.name')}
                value={description}
                onChange={(value) => onUpdatePhase({ description: value })}
              />
            ) : (
              <Text size="300" color="grey1000">
                {description}
              </Text>
            )}
          </Box>
        </Table.Cell>
        <Table.Cell paddingX="s12" colSpan={2}>
          <Box textAlign="right">
            <Text size="300" color="grey1000">
              {`${quantity} h`}
            </Text>
          </Box>
        </Table.Cell>
        <Table.Cell paddingX="s12" colSpan={2} />
        <Table.Cell paddingX="s12" colSpan={2} />
        <Table.Cell paddingX="s12" colSpan={2}>
          <Box textAlign="right">
            <Text size="300" color="grey1000">
              {`${baseProfitPercentage}%`}
            </Text>
          </Box>
        </Table.Cell>
        <Table.Cell paddingX="s12" colSpan={2}>
          <Box textAlign="right">
            <Text size="300" color="grey1000">
              {MoneyWithCurrency({
                optionalDecimals: true,
                amount: baseCostCents,
                currency,
                locale,
              })}
            </Text>
          </Box>
        </Table.Cell>
        <Table.Cell paddingX="s12" colSpan={2}>
          <Box textAlign="right">
            <Text size="300" color="grey1000">
              {MoneyWithCurrency({
                optionalDecimals: true,
                amount: basePriceCents,
                currency,
                locale,
              })}
            </Text>
          </Box>
        </Table.Cell>
        {isDraftStatus && (
          <Table.Cell paddingX="s12" colSpan={1} width={80}>
            <Button type="secondary" iconName="delete" onClick={onDeletePhase} />
          </Table.Cell>
        )}
      </Table.Row>
      {expanded && (
        <>
          {phase.financeLineItems?.map((child) => (
            <ItemRowTimeAndMaterial
              key={child.id}
              item={child}
              currency={currency}
              isDraftStatus={isDraftStatus}
              isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
              onUpdate={(updates) => onUpdateChild(child.id, updates)}
              onDelete={() => onDeleteChild(child.id)}
            />
          ))}
          {isDraftStatus && (
            <Table.Row>
              <Table.Cell colSpan={2}>
                <Box flexDirection="row" justifyContent="flexStart" paddingLeft="s32">
                  <AddItemButton
                    isProductQuote={false}
                    onAddRole={() => onAddItem(phase.id, 'role')}
                    onAddProduct={() => onAddItem(phase.id, 'simple')}
                  />
                </Box>
              </Table.Cell>
              <Table.Cell colSpan={13} />
            </Table.Row>
          )}
        </>
      )}
    </>
  )
}

export default ItemRowPhase
